import { NgClass } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { KeycloakService } from 'keycloak-angular';

import {
  JoinSource,
  SessionJoinService,
} from '../../../core/services/academic-appointments/session-join.service';
import { ExternalNavigationService } from '../../../core/services/utils/external-navigation.service';
import { isNavigableUrl } from '../../../core/utils/external-link';
import { ErrorStateComponent } from '../../ui/error-state/error-state.component';

/** Ce que la page montre, dans l'ordre où ça peut arriver. */
export type SessionJoinState =
  | 'joining'
  | 'redirecting'
  | 'refused'
  | 'not-found'
  | 'error';

/**
 * Le temps de lire « votre présence comptera à partir de … » avant que la
 * visio ne remplace la page. Sans ce délai, le message n'existerait pas.
 */
export const EARLY_JOIN_REDIRECT_DELAY_MS = 3000;

/** Valeur du paramètre `src` que le mail de rappel pose sur son bouton. */
const REMINDER_SOURCE_QUERY = 'reminder';

/**
 * Les refus que le serveur code, et l'icône de chacun. Le libellé n'est pas
 * ici : il vit sous `CALENDAR.ERRORS.<code>`, où l'agenda enseignant traduit
 * déjà les mêmes codes — une phrase par langue, pas deux.
 */
const REFUSAL_ICONS: Record<string, string> = {
  SESSION_CANCELLED: 'fa-ban',
  SESSION_NO_MEETING: 'fa-video-slash',
  SESSION_ENDED: 'fa-clock',
  SESSION_NOT_INVITED: 'fa-user-lock',
};

/**
 * Atterrissage du bouton « Rejoindre la séance » du mail de rappel.
 *
 * <p>Le mail ne porte pas le lien de visio : il mène ici, hors des coquilles
 * par rôle, pour que la présence soit <em>journalisée avant</em> toute
 * redirection — c'est une pièce de facturation, et elle servira aux points des
 * élèves et à la présence des professeurs. La route est gardée par
 * `RoleGuard`, qui conserve l'adresse demandée à travers la connexion Keycloak :
 * un élève qui clique sans être connecté revient ici, et non sur l'accueil.</p>
 *
 * <p>Un clic en avance est accepté et journalisé mais ne compte pas : la page
 * le dit, puis redirige quand même. Un clic trop tard est refusé par le
 * serveur, comme un clic de quelqu'un qui n'est pas convié : chaque refus porte
 * un code, traduit ici dans la langue de l'utilisateur.</p>
 */
@Component({
  selector: 'app-session-join-landing',
  standalone: true,
  imports: [NgClass, RouterLink, TranslateModule, ErrorStateComponent],
  templateUrl: './session-join-landing.component.html',
  styleUrl: './session-join-landing.component.css',
})
export class SessionJoinLandingComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly joins = inject(SessionJoinService);
  private readonly navigation = inject(ExternalNavigationService);
  private readonly keycloak = inject(KeycloakService);

  readonly state = signal<SessionJoinState>('joining');

  /** L'URL de la visio, une fois la présence enregistrée — pour le lien de secours. */
  readonly meetingUrl = signal<string | null>(null);

  /** « HH:MM » à partir de laquelle le clic aurait compté ; `null` quand il compte. */
  readonly countsFrom = signal<string | null>(null);

  /** Le code du refus (`SESSION_ENDED`…), quand le serveur en a donné un. */
  readonly refusalCode = signal<string | null>(null);

  /** Message du serveur pour une erreur sans code connu — mieux que rien. */
  readonly detail = signal<string>('');

  /** Le libellé du refus : la même clé que l'agenda enseignant pour le même code. */
  readonly refusalKey = computed(() => `CALENDAR.ERRORS.${this.refusalCode() ?? ''}`);

  /** Icône de l'état courant — Font Awesome, comme le reste des pages hors coquille. */
  readonly icon = computed(() => {
    switch (this.state()) {
      case 'joining':
        return 'fa-spinner fa-spin';
      case 'redirecting':
        return 'fa-circle-check';
      case 'refused':
        return REFUSAL_ICONS[this.refusalCode() ?? ''] ?? 'fa-circle-xmark';
      case 'not-found':
        return 'fa-magnifying-glass';
      default:
        return 'fa-triangle-exclamation';
    }
  });

  /**
   * La page est arrivée au bout : rien ne va plus se passer tout seul. C'est
   * là, et là seulement, qu'on propose une sortie — pendant une redirection,
   * un lien cliqué perdrait la course contre la navigation déjà engagée.
   */
  readonly isTerminal = computed(() => this.state() !== 'joining' && this.state() !== 'redirecting');

  /** L'agenda du rôle, comme porte de sortie. */
  readonly agendaLink = agendaFor(this.roles());

  private redirectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Quitter la page pendant le délai de lecture ne doit pas emporter l'onglet
    // vers la visio trois secondes plus tard, depuis l'agenda.
    inject(DestroyRef).onDestroy(() => this.cancelPendingRedirect());
  }

  ngOnInit(): void {
    this.join();
  }

  /** Journalise la présence puis remplace la page par la visio. Relançable depuis l'état d'erreur. */
  join(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isInteger(id) || id <= 0) {
      this.state.set('not-found');
      return;
    }

    this.state.set('joining');
    this.detail.set('');
    this.refusalCode.set(null);

    this.joins.join(id, this.source()).subscribe({
      next: (res) => this.onJoined(res?.data),
      error: (err: unknown) => this.onRefused(err),
    });
  }

  private source(): JoinSource {
    return this.route.snapshot.queryParamMap.get('src') === REMINDER_SOURCE_QUERY
      ? 'EmailReminder'
      : 'App';
  }

  private onJoined(data: { meetingUrl?: string | null; counted?: boolean; countsFrom?: string } | undefined): void {
    const url = data?.meetingUrl;
    if (!isNavigableUrl(url)) {
      // Le serveur a journalisé sans rien à ouvrir : même situation que son
      // refus explicite, même libellé. Un lien qui n'est pas `http(s)` —
      // saisi avant que le serveur ne le contrôle — n'ouvre rien non plus, et
      // ne doit pas même apparaître comme lien de secours.
      this.refusalCode.set('SESSION_NO_MEETING');
      this.state.set('refused');
      return;
    }

    this.meetingUrl.set(url);
    this.state.set('redirecting');

    // La présence est enregistrée : la redirection peut suivre. Quand le clic
    // ne compte pas encore, on laisse le temps de lire à partir de quand.
    if (data?.counted === false && data.countsFrom) {
      this.countsFrom.set(hourOf(data.countsFrom));
      this.redirectTimer = setTimeout(() => this.navigation.assign(url), EARLY_JOIN_REDIRECT_DELAY_MS);
      return;
    }

    this.navigation.assign(url);
  }

  private onRefused(err: unknown): void {
    const response = err as { status?: number; error?: { code?: string; message?: string } };
    const code = response?.error?.code;

    if (code && code in REFUSAL_ICONS) {
      this.refusalCode.set(code);
      this.state.set('refused');
      return;
    }

    // Séance inconnue, ou hors du périmètre d'un inspecteur : le serveur dit
    // « introuvable » dans les deux cas, et réessayer n'y changerait rien.
    if (response?.status === 404) {
      this.state.set('not-found');
      return;
    }

    this.detail.set(response?.error?.message ?? '');
    this.state.set('error');
  }

  private cancelPendingRedirect(): void {
    if (this.redirectTimer !== null) {
      clearTimeout(this.redirectTimer);
      this.redirectTimer = null;
    }
  }

  private roles(): string[] {
    try {
      return this.keycloak.getUserRoles(true);
    } catch {
      return [];
    }
  }
}

/**
 * L'agenda qui correspond au rôle. Un compte peut en porter plusieurs : l'élève
 * d'abord, puis l'enseignant, puis l'encadrement — l'ordre des coquilles.
 */
export function agendaFor(roles: readonly string[]): string {
  if (roles.includes('student')) return '/student/agenda';
  if (roles.includes('professor') || roles.includes('teacher')) return '/teacher/calendar';
  if (roles.includes('supervisor')) return '/admin/calendar';
  if (roles.includes('sessions.inspect')) return '/inspector/calendar';
  return '/';
}

/**
 * « HH:MM » d'une heure locale de l'école (`2026-10-06T08:30:00`), sans passer
 * par `Date` : le navigateur l'interpréterait dans son propre fuseau.
 */
export function hourOf(localDateTime: string): string {
  const match = /T(\d{2}):(\d{2})/.exec(localDateTime);
  return match ? `${match[1]}:${match[2]}` : localDateTime;
}
